/**
 * Blog CMS Integration Module
 * Exclusively interfaces with Payload CMS (https://206.189.91.184.sslip.io)
 * No static mock data - all posts are retrieved dynamically from the live CMS.
 */

export const CMS_API_URL = 'https://206.189.91.184.sslip.io/api/posts';
export const CMS_ORIGIN = 'https://206.189.91.184.sslip.io';
export const DEFAULT_HERO_IMAGE =
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80';

// Keep one CMS request per page session and reuse it when opening an article.
let publishedArticlesPromise;
let publishedArticlesCache;

export const getImageSource = (image) => {
  if (!image) return { url: '' };
  if (typeof image === 'string') {
    if (image.startsWith('//')) return { url: `https:${image}` };
    if (/^https?:\/\//i.test(image)) return { url: image };
    return { url: `${CMS_ORIGIN}/${image.replace(/^\/+/, '')}` };
  }

  // Prefer an appropriately sized CMS rendition so cards do not download huge originals.
  let imageUrl = '';
  if (image.sizes) {
    const sorted = Object.values(image.sizes)
      .filter((s) => s?.url && s.width)
      .sort((a, b) => (a.width || 0) - (b.width || 0));
    imageUrl = sorted.find((size) => size.width >= 1200)?.url || sorted[sorted.length - 1]?.url || '';
  }
  imageUrl = imageUrl || image.url || '';

  if (imageUrl.startsWith('//')) {
    imageUrl = `https:${imageUrl}`;
  } else if (imageUrl && !/^https?:\/\//i.test(imageUrl)) {
    imageUrl = `${CMS_ORIGIN}/${imageUrl.replace(/^\/+/, '')}`;
  }
  return { url: imageUrl };
};

export const getImageUrl = (image) => getImageSource(image).url;

export const getAuthor = (post) => {
  const author = post?.populatedAuthors?.[0] || post?.authors?.[0];
  const name =
    typeof author === 'string'
      ? author
      : author?.name || author?.fullName || 'Coalitionify Team';
  const role =
    typeof author === 'object'
      ? author?.role || author?.title || 'Real Estate '
      : 'Real Estate';
  const avatarUrl =
    typeof author === 'object' && author?.avatar ? getImageUrl(author.avatar) : '';
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase() || 'CT';

  return {
    name,
    role,
    avatarUrl,
    initials,
  };
};

export const slugify = (text) => {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
};

export const getNodePlainText = (node) => {
  if (!node) return '';
  if (typeof node.text === 'string') return node.text;
  if (Array.isArray(node.children)) {
    return node.children.map(getNodePlainText).join('');
  }
  return '';
};

export const lexicalNodeToHtml = (node) => {
  if (!node) return '';

  // Text leaf
  if (node.text !== undefined) {
    let text = String(node.text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Formats bitmask: 1=bold, 2=italic, 4=strikethrough, 8=underline, 16=code
    if (node.format & 1) text = `<strong>${text}</strong>`;
    if (node.format & 2) text = `<em>${text}</em>`;
    if (node.format & 4) text = `<del>${text}</del>`;
    if (node.format & 8) text = `<u>${text}</u>`;
    if (node.format & 16) text = `<code>${text}</code>`;
    return text;
  }

  const children = (node.children || []).map(lexicalNodeToHtml).join('');

  if (node.type === 'heading') {
    const rawText = getNodePlainText(node);
    const id = slugify(rawText);
    const tag = node.tag || 'h2';
    return `<${tag} id="${id}">${children}</${tag}>`;
  }

  if (node.type === 'quote') {
    return `<blockquote>${children}</blockquote>`;
  }

  if (node.type === 'list') {
    const listTag = node.listType === 'number' ? 'ol' : 'ul';
    return `<${listTag}>${children}</${listTag}>`;
  }

  if (node.type === 'listitem') {
    return `<li>${children}</li>`;
  }

  if (node.type === 'link') {
    const href = node.fields?.url || node.url || '#';
    const target = node.fields?.newTab ? '_blank' : '_self';
    return `<a href="${href}" target="${target}" rel="noopener noreferrer">${children}</a>`;
  }

  if (node.type === 'paragraph') {
    return `<p>${children}</p>`;
  }

  if (node.type === 'upload') {
    const mediaObj = node.value;
    const imgUrl = getImageUrl(mediaObj);
    if (imgUrl) {
      return `<figure class="blog-media"><img src="${imgUrl}" alt="${mediaObj?.alt || ''}" class="rounded-xl w-full my-6" />${
        mediaObj?.caption ? `<figcaption>${mediaObj.caption}</figcaption>` : ''
      }</figure>`;
    }
    return '';
  }

  if (node.type === 'horizontalrule') {
    return `<hr />`;
  }

  return children;
};

export const extractHeadingsFromAst = (node, headings = []) => {
  if (!node) return headings;
  // Only extract top-level h2 headings for a clean TOC (skip h3+)
  if (node.type === 'heading' && (node.tag === 'h2')) {
    const text = getNodePlainText(node);
    if (text && text.length > 2) {
      headings.push({
        id: slugify(text),
        title: text,
        level: node.tag,
      });
    }
  }
  if (Array.isArray(node.children)) {
    node.children.forEach((child) => extractHeadingsFromAst(child, headings));
  }
  return headings;
};

export const getContentHtml = (content) => {
  if (!content) return '<p>Article content is unavailable.</p>';
  if (typeof content === 'string') return content;
  return lexicalNodeToHtml(content.root || content);
};

export const extractHeadingsFromHtml = (html) => {
  if (!html) return [];
  const headings = [];
  const regex = /<h([2-3])[^>]*id=["']([^"']+)["'][^>]*>(.*?)<\/h\1>/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const level = `h${match[1]}`;
    const id = match[2];
    const title = match[3].replace(/<[^>]+>/g, '').trim();
    if (title) {
      headings.push({ id, title, level });
    }
  }
  return headings;
};

export const mapCmsPost = (post, index = 0) => {
  const category =
    post.categories?.[0]?.title ||
    post.categories?.[0]?.name ||
    'General';

  const contentHtml = getContentHtml(post.content);
  const plainText = contentHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const wordCount = plainText.split(/\s+/).filter(Boolean).length;
  const readTime = `${Math.max(1, Math.ceil(wordCount / 200))} min read`;

  let headings = [];
  if (post.content?.root) {
    headings = extractHeadingsFromAst(post.content.root);
  }
  if (!headings.length) {
    headings = extractHeadingsFromHtml(contentHtml);
  }

  const title = post.title || post.slug || 'Untitled article';
  const author = getAuthor(post);
  const imageSource = getImageSource(post.heroImage);

  // High-quality default real estate/tech editorial image if heroImage is empty
  const publishedDate = post.publishedAt
    ? `Updated ${new Date(post.publishedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}`
    : 'Recently published';

  const rawExcerpt = post.meta?.description || plainText;
  const excerpt = rawExcerpt.length > 200 ? rawExcerpt.slice(0, 197) + '...' : rawExcerpt;

  const tags =
    post.tags?.map((tag) => (typeof tag === 'string' ? tag : tag.title || tag.name)).filter(Boolean) || [category];

  return {
    id: post.id || post.slug || `cms-post-${index}`,
    title,
    slug: post.slug || '',
    category,
    readTime,
    publishedDate,
    rawPublishedAt: post.publishedAt,
    author,
    featured: index === 0,
    heroImage: imageSource.url || DEFAULT_HERO_IMAGE,
    excerpt,
    contentHtml,
    headings,
    tags,
  };
};

export const fetchPublishedArticles = async () => {
  if (publishedArticlesCache) return publishedArticlesCache;
  if (!publishedArticlesPromise) {
    const query = new URLSearchParams({
      'where[_status][equals]': 'published',
      depth: '1',
      limit: '100',
    });
    // Let the shared request finish even if the list view unmounts during navigation.
    publishedArticlesPromise = fetch(`${CMS_API_URL}?${query.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch articles (${res.status})`);
        return res.json();
      })
      .then((data) => {
        publishedArticlesCache = (data.docs || []).map(mapCmsPost);
        return publishedArticlesCache;
      })
      .catch((error) => {
        publishedArticlesPromise = undefined;
        throw error;
      });
  }
  return publishedArticlesPromise;
};

export const fetchArticleBySlug = async (slug, signal) => {
  const cached = publishedArticlesCache?.find((article) => article.slug === slug);
  if (cached) return cached;
  const query = new URLSearchParams({
    'where[slug][equals]': slug,
    depth: '2',
  });
  const res = await fetch(`${CMS_API_URL}?${query.toString()}`, { signal });
  if (!res.ok) {
    throw new Error(`Failed to fetch article (${res.status})`);
  }
  const data = await res.json();
  if (data.docs && data.docs.length > 0) {
    return mapCmsPost(data.docs[0]);
  }
  return null;
};
